// Couplage report — the chargement × planned-lot matrix as it
// stands at planning time. The report is intentionally a planning
// artifact: it never reflects fabricated output (no actual weights,
// no swapped-in lot numbers). Once lots are milled, the per-lot
// actuals live in the Freinte rapport instead.
//
//   ┌───────────────────────────────────────────┬──── Lots planifiés ──────────┐
//   │ DATE FOURNISSEUR N° CAMIONS Nbre Poids …  │ LP 1 LP 2 … LP N RESTE       │
//   ├───────────────────────────────────────────┼──────────────────────────────┤
//   │ (per-chargement rows)                     │ (per-(chargement, lot) bags) │
//   ├ TOTAL SACS ─────────────────────────────  │ (per-lot bag totals)         │
//   └─────────────────────────────────────────────────────────────────────────┘
//
// The same shape powers the on-screen panel + the PDF + the Excel
// export.
import type {
  CouplingLoadView,
  PlannedLotRow,
  UsinageDetail,
} from "@/features/milling/api/types"

// One row of the left ledger — a chargement (LOAD) or a virtual
// chargement (QUEUE_LOT) attached to the usinage.
export interface CouplageReportSource {
  kind: "LOAD" | "QUEUE_LOT"
  id: number
  fournisseur: string         // cooperative name (LOAD) / "Queue de Lot" (QDL)
  cccNumber: string           // DeliveryForm BoL number (LOAD) / lot number (QDL — no CCC)
  nbreSacs: number            // bagsAssigned
  poidsNetKg: number          // weight share contributed to this usinage
  poidsMoyenneKg: number      // poidsNetKg / nbreSacs (per-bag avg)
}

// One column of the matrix — a planned lot. Carries only planning
// data: no fabricated lot number, no actual weight.
export interface CouplageReportLot {
  id: string                  // planned-lot UUID (matrix key)
  label: string               // operator-entered planned label, e.g. "LOT PLANNED 1"
  // Per-row bag counts for this column, indexed by composite source key
  // "<kind>:<id>" (matches lib/couplage-validation.ts).
  bagsBySource: Record<string, number>
  totalBags: number           // Σ bagsBySource (== plannedBags)
}

export interface CouplageReportData {
  usinageNumber: string
  // Date range — first → last arrived chargement date, falling back
  // to the usinage start date if no arrivals carry a date.
  dateRange: { from: string; to: string }

  sources: CouplageReportSource[]
  lots: CouplageReportLot[]
  // Computed: per-source residual = nbreSacs − Σ(matrix row),
  // matches the paper's "RESTE" column.
  resteBySource: Record<string, number>
  // Per-column total of nbreSacs across all sources for that column.
  // Distinct from `lot.totalBags` only when a column has bags from
  // sources not in the visible matrix — which doesn't currently
  // happen but the separation keeps the intent clear.
  totalBagsByLot: Record<string, number>
  // Totals row.
  totalNbreSacs: number
  totalPoidsNetKg: number
  totalPoidsMoyenneKg: number  // weighted avg across all sources
  totalResteSacs: number
}

export function buildCouplageReport(
  usinage: UsinageDetail,
  loads: CouplingLoadView[],
): CouplageReportData {
  const sources = buildSources(usinage, loads)
  const lots = buildLots(usinage.lotsPlanned ?? [])
  const resteBySource = computeReste(sources, lots)
  const totalBagsByLot = computeTotalBagsByLot(lots)

  const totalNbreSacs = sources.reduce((a, s) => a + s.nbreSacs, 0)
  const totalPoidsNetKg = sources.reduce((a, s) => a + s.poidsNetKg, 0)
  const totalPoidsMoyenneKg =
    totalNbreSacs > 0 ? totalPoidsNetKg / totalNbreSacs : 0
  const totalResteSacs = Object.values(resteBySource).reduce(
    (a, n) => a + n,
    0,
  )

  return {
    usinageNumber: usinage.usinageNumber,
    dateRange: computeDateRange(sources, usinage),
    sources,
    lots,
    resteBySource,
    totalBagsByLot,
    totalNbreSacs,
    totalPoidsNetKg,
    totalPoidsMoyenneKg,
    totalResteSacs,
  }
}

function buildSources(
  usinage: UsinageDetail,
  loads: CouplingLoadView[],
): CouplageReportSource[] {
  const out: CouplageReportSource[] = []
  for (const a of usinage.assignments ?? []) {
    const load = a.load
    const view = loads.find(
      (l) => l.sourceKind === "LOAD" && l.loadId === a.loadId,
    )
    const totalBags = load?.bagsUnloaded ?? 0
    const share = totalBags > 0 ? (a.bagsAssigned ?? 0) / totalBags : 0
    const poidsNetKg = (load?.netWeight ?? 0) * share
    const nbreSacs = a.bagsAssigned ?? 0
    out.push({
      kind: "LOAD",
      id: a.loadId,
      fournisseur:
        view?.cooperativeName ?? load?.Orders?.groupId?.name ?? "—",
      cccNumber:
        view?.deliverySlip ??
        load?.DeliveryForms?.billOfLadingNumber ??
        `#${load?.loadNumber ?? a.loadId}`,
      nbreSacs,
      poidsNetKg,
      poidsMoyenneKg: nbreSacs > 0 ? poidsNetKg / nbreSacs : 0,
    })
  }
  for (const a of usinage.qdlAssignments ?? []) {
    const view = loads.find(
      (l) => l.sourceKind === "QUEUE_LOT" && l.loadId === a.queueLotId,
    )
    const avg = a.avgBagKg ?? view?.avgBagKg ?? 0
    const nbreSacs = a.bagsAssigned ?? 0
    out.push({
      kind: "QUEUE_LOT",
      id: a.queueLotId,
      fournisseur: "Queue de Lot",
      cccNumber: view?.loadNumber ?? `#${a.queueLotId}`,
      nbreSacs,
      poidsNetKg: nbreSacs * avg,
      poidsMoyenneKg: avg,
    })
  }
  return out
}

function buildLots(planned: PlannedLotRow[]): CouplageReportLot[] {
  return planned.map((p) => {
    const bagsBySource: Record<string, number> = {}
    let totalBags = 0
    for (const c of p.cells) {
      const kind = c.sourceKind ?? "LOAD"
      const key = `${kind}:${c.loadId}`
      bagsBySource[key] = (bagsBySource[key] ?? 0) + c.bags
      totalBags += c.bags
    }
    return {
      id: p.id,
      label: p.label,
      bagsBySource,
      totalBags,
    }
  })
}

function computeReste(
  sources: CouplageReportSource[],
  lots: CouplageReportLot[],
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const s of sources) {
    const key = `${s.kind}:${s.id}`
    let routed = 0
    for (const lot of lots) routed += lot.bagsBySource[key] ?? 0
    out[key] = Math.max(0, s.nbreSacs - routed)
  }
  return out
}

function computeTotalBagsByLot(
  lots: CouplageReportLot[],
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const lot of lots) {
    out[lot.id] = lot.totalBags
  }
  return out
}

function computeDateRange(
  _sources: CouplageReportSource[],
  usinage: UsinageDetail,
): { from: string; to: string } {
  // arrivedAt isn't on the source row — we'd need to re-thread it
  // from the embed. For now use usinage start/end.
  const start = (usinage.startDate ?? "").slice(0, 10)
  const end = (usinage.endDate ?? usinage.startDate ?? "").slice(0, 10)
  return { from: start, to: end }
}

// Composite source key — kept in sync with lib/couplage-validation's
// `sourceKey()`. Exported so the panel can build them from a
// CouplingLoadView without importing the validation lib.
export function reportSourceKey(
  kind: "LOAD" | "QUEUE_LOT",
  id: number,
): string {
  return `${kind}:${id}`
}
